import type { Row } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { isNil } from 'es-toolkit';

import type {
  AvatarCellConfig,
  BadgeCellConfig,
  ButtonCellConfig,
  CellRenderer,
  CheckboxCellConfig,
  DateCellConfig,
  DateFormatOptions,
  DateTimeCellConfig,
  DropdownCellConfig,
  EllipsisCellConfig,
  ExtractResult,
  IconCellConfig,
  InferBadgeCellValue,
  InferButtonCellValue,
  InferDropdownCellValue,
  InferEllipsisCellValue,
  InferTagCellValue,
  InputCellConfig,
  NumberCellConfig,
  NumberFormatOptions,
  TagCellConfig,
  TagType,
  TextCellConfig,
  TimeCellConfig,
} from '../types/cell-types';
import { AvatarCellContent } from '../components/cells/avatar-cell-content';
import { BadgeCellContent } from '../components/cells/badge-cell-content';
import { ButtonCellContent } from '../components/cells/button-cell-content';
import { CheckboxCellContent } from '../components/cells/checkbox-cell-content';
import { DateCellContent } from '../components/cells/date-cell-content';
import { DropdownCellContent } from '../components/cells/dropdown-cell-content';
import { EllipsisCellContent } from '../components/cells/ellipsis-cell-content';
import { IconCellContent } from '../components/cells/icon-cell-content';
import { InputCellContent } from '../components/cells/input-cell-content';
import { NumberCellContent } from '../components/cells/number-cell-content';
import { TagCellContent } from '../components/cells/tag-cell-content';
import { TextCellContent } from '../components/cells/text-cell-content';
import { useDataTableContext } from '../components/data-table-context';

function isRecord<TResult>(value: unknown): value is Record<PropertyKey, TResult> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function resolveValue<TData, TInput>(
  valueOrGetter: TInput,
  row: Row<TData>,
  cellValue: unknown,
): ExtractResult<TInput> | undefined {
  if (typeof valueOrGetter === 'function') {
    return (valueOrGetter as (row: Row<TData>) => ExtractResult<TInput>)(row);
  }

  if (isRecord(valueOrGetter) && !isNil(cellValue)) {
    return (valueOrGetter as Record<PropertyKey, ExtractResult<TInput>>)[cellValue as PropertyKey];
  }

  return valueOrGetter as ExtractResult<TInput>;
}

function formatNumber(value: number | string, options?: NumberFormatOptions): string {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(numValue)) return String(value);

  const { locale = 'en-US', ...intlOptions } = options ?? {};
  return new Intl.NumberFormat(locale, intlOptions).format(numValue);
}

function formatDate(value: Date | string | number, options?: DateFormatOptions): string {
  const dateValue = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateValue.getTime())) return String(value);

  const { locale = 'en-US', ...intlOptions } = options ?? {};
  return new Intl.DateTimeFormat(locale, intlOptions).format(dateValue);
}

export function createTextCell<TData, TValue>(config: TextCellConfig<TData> = {}): CellRenderer<TData, TValue> {
  return ({ row, getValue }) => {
    const value = config.getValue?.(row) ?? (getValue() as ReactNode);
    const description = config.getDescription?.(row);

    return <TextCellContent value={value} description={description} className={config.className} />;
  };
}

export function createNumberCell<TData, TValue>(config: NumberCellConfig<TData> = {}): CellRenderer<TData, TValue> {
  return ({ row, getValue }) => {
    const rawValue = config.getValue?.(row) ?? getValue();
    let displayValue: number | string;

    if (!isNil(config.format)) {
      displayValue =
        typeof config.format === 'function'
          ? config.format(rawValue as number | string)
          : formatNumber(rawValue as number | string, config.format);
    } else {
      displayValue = rawValue as number | string;
    }

    const description = config.getDescription?.(row);

    return <NumberCellContent value={displayValue} description={description} className={config.className} />;
  };
}

export function createDateCell<TData, TValue>(config: DateCellConfig<TData> = {}): CellRenderer<TData, TValue> {
  return ({ row, getValue }) => {
    const rawValue = config.getValue?.(row) ?? getValue();
    const displayValue = formatDate(rawValue as Date | string | number, {
      dateStyle: 'medium',
      ...config.format,
    });
    const description = config.getDescription?.(row);

    return <DateCellContent value={displayValue} description={description} className={config.className} />;
  };
}

export function createTimeCell<TData, TValue>(config: TimeCellConfig<TData> = {}): CellRenderer<TData, TValue> {
  return ({ row, getValue }) => {
    const rawValue = config.getValue?.(row) ?? getValue();
    const displayValue = formatDate(rawValue as Date | string | number, {
      timeStyle: 'short',
      ...config.format,
    });
    const description = config.getDescription?.(row);

    return <DateCellContent value={displayValue} description={description} className={config.className} />;
  };
}

export function createDateTimeCell<TData, TValue>(config: DateTimeCellConfig<TData> = {}): CellRenderer<TData, TValue> {
  return ({ row, getValue }) => {
    const rawValue = config.getValue?.(row) ?? getValue();
    const displayValue = formatDate(rawValue as Date | string | number, {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...config.format,
    });
    const description = config.getDescription?.(row);

    return <DateCellContent value={displayValue} description={description} className={config.className} />;
  };
}

export function createBadgeCell<TData, const TConfig extends BadgeCellConfig<TData>>(
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Default config value
  config: TConfig = {} as TConfig,
): CellRenderer<TData, InferBadgeCellValue<TConfig>> {
  return ({ row, getValue }) => {
    const cellValue = getValue();
    const label = config.getValue?.(row) ?? (cellValue as ReactNode);
    const variant = resolveValue(config.variant, row, cellValue);
    const type = resolveValue(config.type, row, cellValue);

    return <BadgeCellContent label={label} variant={variant} type={type} className={config.className} />;
  };
}

export function createAvatarCell<TData, TValue>(config: AvatarCellConfig<TData> = {}): CellRenderer<TData, TValue> {
  return ({ row }) => {
    const imageUrl = config.getImageUrl?.(row);
    const firstName = config.getFirstName?.(row);
    const lastName = config.getLastName?.(row);
    const value = config.getValue?.(row);
    const description = config.getDescription?.(row);

    return (
      <AvatarCellContent
        imageUrl={imageUrl}
        firstName={firstName}
        lastName={lastName}
        value={value}
        description={description}
        size={config.size}
        className={config.className}
      />
    );
  };
}

export function createIconCell<TData, TValue>(config: IconCellConfig<TData>): CellRenderer<TData, TValue> {
  return ({ row, getValue }) => {
    const icon = resolveValue(config.icon, row, getValue());
    const description = config.getDescription?.(row);

    return <IconCellContent icon={icon} description={description} className={config.className} />;
  };
}

export function createButtonCell<TData, const TConfig extends ButtonCellConfig<TData>>(
  config: TConfig,
): CellRenderer<TData, InferButtonCellValue<TConfig>> {
  return ({ row, getValue }) => {
    const cellValue = getValue();
    const label = config.getLabel(row);
    const variant = resolveValue(config.variant, row, cellValue);
    const size = resolveValue(config.size, row, cellValue);
    const icon = config.getIcon?.(row);
    const disabled = config.getDisabled?.(row);
    const isLoading = config.getIsLoading?.(row);

    return (
      <ButtonCellContent
        label={label}
        onClick={() => config.onClick(row)}
        variant={variant}
        size={size}
        icon={icon}
        iconPosition={config.iconPosition}
        disabled={disabled}
        isLoading={isLoading}
        className={config.className}
      />
    );
  };
}

export function createDropdownCell<TData, const TConfig extends DropdownCellConfig<TData>>(
  config: TConfig,
): CellRenderer<TData, InferDropdownCellValue<TConfig>> {
  return ({ row, getValue }) => {
    const cellValue = getValue();
    const label = config.getLabel(row);
    const items = config.getItems(row);
    const variant = resolveValue(config.variant, row, cellValue);
    const size = resolveValue(config.size, row, cellValue);

    return (
      <DropdownCellContent
        label={label}
        items={items}
        row={row}
        variant={variant}
        size={size}
        className={config.className}
      />
    );
  };
}

export function createEllipsisCell<TData, const TConfig extends EllipsisCellConfig<TData>>(
  config: TConfig,
): CellRenderer<TData, InferEllipsisCellValue<TConfig>> {
  return ({ row, getValue }) => {
    const cellValue = getValue();
    const items = config.getItems(row);
    const variant = resolveValue(config.variant, row, cellValue);
    const size = resolveValue(config.size, row, cellValue);

    return <EllipsisCellContent items={items} row={row} variant={variant} size={size} className={config.className} />;
  };
}

export function createTagCell<TData, const TConfig extends TagCellConfig<TData>>(
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Default config value
  config: TConfig = {} as TConfig,
): CellRenderer<TData, InferTagCellValue<TConfig>> {
  return ({ row, getValue }) => {
    const cellValue = getValue();
    const label = config.getLabel?.(row) ?? (cellValue as string);
    const type: TagType = resolveValue(config.type, row, cellValue) ?? 'read-only';
    const color: string | undefined = resolveValue(config.color, row, cellValue);

    return (
      <TagCellContent
        label={label}
        type={type}
        color={color}
        onRemove={!isNil(config.onRemove) ? () => config.onRemove?.(row) : undefined}
        className={config.className}
      />
    );
  };
}

export function createInputCell<TData, TValue>(config: InputCellConfig<TData> = {}): CellRenderer<TData, TValue> {
  return ({ row, getValue, column }) => {
    const cellValue = getValue();
    const value = config.getValue?.(row) ?? (cellValue as string);
    const placeholder = config.getPlaceholder?.(row);
    const disabled = config.getDisabled?.(row);

    const { updateCell } = useDataTableContext<TData>();

    return (
      <InputCellContent
        value={value}
        onChange={(newValue) => {
          config.onChange?.(row, newValue);
          updateCell(row.id, column.id as keyof TData, newValue as TData[keyof TData]);
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={config.className}
      />
    );
  };
}

export function createCheckboxCell<TData, TValue>(config: CheckboxCellConfig<TData> = {}): CellRenderer<TData, TValue> {
  return ({ row, getValue, column }) => {
    const cellValue = getValue();
    const checked = config.getChecked?.(row) ?? (cellValue as boolean);
    const label = config.getLabel?.(row);
    const description = config.getDescription?.(row);
    const disabled = config.getDisabled?.(row);

    const { updateCell } = useDataTableContext<TData>();

    return (
      <CheckboxCellContent
        checked={checked}
        onCheckedChange={(newChecked) => {
          config.onCheckedChange?.(row, newChecked);
          updateCell(row.id, column.id as keyof TData, newChecked as TData[keyof TData]);
        }}
        label={label}
        description={description}
        disabled={disabled}
        className={config.className}
      />
    );
  };
}
